import apiClient from '@/api/axiosClient';
import type { AxiosError } from 'axios';
import type {
  PrimaryTypeChangeRequest,
  PrimaryTypeChangeResponse
} from '@/api/contracts/tableDefinition';
import type { components } from '@/api/types/auto-generated';

// Re-export types from auto-generated for use in components
export type NewColumnRequest = components['schemas']['NewColumnRequest'];
export type UpdateColumnDefinitionRequest = components['schemas']['UpdateColumnDefinitionRequest'];
export type NullHandling = components['schemas']['NullHandling'];

// DefaultFunction from backend - bruker enum verdier direkte (none, utcNow, newId, nextRowId, identity)
// Når ingen funksjon er valgt: ikke send feltet (undefined i TypeScript)
export type DefaultFunction = components['schemas']['DefaultFunction'];

export interface AddColumnPreflightResult {
  rowCount: number;
  needsFillValue: boolean;
}

export interface AddColumnResult {
  success: boolean;
  errors?: Record<string, string[]>;
}

/**
 * Legg til ny kolonne i en tabell
 * Returnerer { success: true } ved suksess, eller { success: false, errors: {...} } ved valideringsfeil
 */
export async function addTableColumn (
  tableDefinitionId: number,
  request: NewColumnRequest,
  signal?: AbortSignal
): Promise<AddColumnResult> {
  try {
    await apiClient.post(`v2/table/column/${tableDefinitionId}`, request, { signal });
    return { success: true };
  } catch (error) {
    const axiosError = error as AxiosError<{ errors?: Record<string, string[]>; detail?: string; title?: string }>;
    if (axiosError.response?.status === 400) {
      // Håndter både FluentValidation errors-format og ProblemDetails-format
      if (axiosError.response.data?.errors) {
        return { success: false, errors: axiosError.response.data.errors };
      }
      // ProblemDetails format (title/detail)
      const detail = axiosError.response.data?.detail || axiosError.response.data?.title || 'Ukjent valideringsfeil';
      return { success: false, errors: { general: [detail] } };
    }
    throw error;
  }
}

/**
 * Preflight for AddColumn - sjekker om tabellen har data
 */
export async function addColumnPreflight (
  tableDefinitionId: number,
  nullHandling: string,
  signal?: AbortSignal
): Promise<AddColumnPreflightResult> {
  const response = await apiClient.get<AddColumnPreflightResult>(
    `v2/table/column/${tableDefinitionId}/preflight`,
    { params: { nullHandling }, signal }
  );
  return response.data;
}

/**
 * Slett en kolonne fra en tabell
 */
export async function deleteTableColumn (
  tableDefinitionId: number,
  columnName: string,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.delete(`v2/table/column/${tableDefinitionId}`, {
    params: { columnName },
    signal
  });
}

// ===== Column modification operations =====
// Re-export types from auto-generated for use in components
export type UpdateLengthRequest = components['schemas']['UpdateLengthRequest'];
export type SetNullRequest = components['schemas']['SetNullRequest'];
export type SetNotNullRequest = components['schemas']['SetNotNullRequest'];
export type RequiredPreflightRequest = components['schemas']['RequiredPreflightRequest'];
export type RequiredApplyRequest = components['schemas']['RequiredApplyRequest'];
export type RequiredPreflightResponse = components['schemas']['RequiredPreflightResponse'];

/**
 * Oppdater kolonnelengde
 */
export async function updateColumnLength (
  request: UpdateLengthRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/update-length', request, { signal });
}

/**
 * Sett kolonne til nullable
 */
export async function setColumnNull (
  request: SetNullRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/set-null', request, { signal });
}

/**
 * Sett kolonne til NOT NULL
 */
export async function setColumnNotNull (
  request: SetNotNullRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/set-not-null', request, { signal });
}

/**
 * Preflight for required column - sjekker om tabellen har data som trenger fill-verdi
 */
export async function requiredColumnPreflight (
  request: RequiredPreflightRequest,
  signal?: AbortSignal
): Promise<RequiredPreflightResponse> {
  const response = await apiClient.post<RequiredPreflightResponse>('v2/table/column/required/preflight', request, { signal });
  return response.data;
}

/**
 * Sett kolonne som required med fill-verdi for eksisterende rader
 */
export async function requiredColumnApply (
  request: RequiredApplyRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/required/apply', request, { signal });
}

/**
 * Utfør primærtype-endring for en kolonne
 */
export async function changePrimaryType (
  tableDefinitionId: number,
  columnName: string,
  request: PrimaryTypeChangeRequest
): Promise<PrimaryTypeChangeResponse> {
  const response = await apiClient.put<PrimaryTypeChangeResponse>(
    `v2/table/column/${tableDefinitionId}/${columnName}/primary-type`,
    request
  );
  return response.data;
}

/**
 * Oppdater kun kolonnens label (visningsnavn)
 * Bruker dedikert endpoint som ikke krever full payload
 */
export async function updateColumnLabel (
  tableDefinitionId: number,
  columnName: string,
  newLabel: string,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.put(
    'v2/table/column/label',
    null,
    { params: { tableDefinitionId, columnName, newValue: newLabel }, signal }
  );
}
